import { useMediaQuery, useTheme } from "@mui/material";

/**
 * Hook responsivo padrao da aplicacao (WhatsApp-like).
 *
 * Breakpoints alinhados com `shared/styles/responsive.scss`:
 *   - small  ≤ 640px  (mobile retrato)
 *   - medium ≤ 768px  (mobile paisagem / tablet pequeno) ← MOBILE_BREAKPOINT
 *   - large  ≤ 1024px (tablet)
 *   - xlarge ≤ 1280px (desktop pequeno)
 *
 * useIsMobile() = true quando largura ≤ 768px. Use para:
 *   - Esconder sidebars laterais e mostrar BottomNav
 *   - Empilhar Content full-screen em vez de coluna lado-a-lado
 *   - Renderizar Drawer/Sheet em vez de Modal centrado
 *
 * Combina com MUI useMediaQuery para reativo (SSR-safe, ouve resize).
 *
 * @example
 *   const isMobile = useIsMobile();
 *   return isMobile ? <MobileLayout /> : <DesktopLayout />;
 */
export const MOBILE_BREAKPOINT_PX = 768;

export function useIsMobile(): boolean {
	return useMediaQuery(`(max-width: ${MOBILE_BREAKPOINT_PX}px)`, { noSsr: true });
}

/**
 * Variante mais restrita — mobile pequeno (telas ≤ 640px, ex: iPhone SE).
 * Use quando precisar esconder coisas extras alem do que useIsMobile ja esconde.
 */
export function useIsSmallMobile(): boolean {
	return useMediaQuery("(max-width: 640px)", { noSsr: true });
}

/**
 * Tablet (entre 768 e 1024) — usado raramente, p/ ajustes especificos
 * tipo grid de 2 colunas em vez de 1 (mobile) ou 3 (desktop).
 */
export function useIsTablet(): boolean {
	return useMediaQuery("(min-width: 769px) and (max-width: 1024px)", { noSsr: true });
}

/**
 * Wrapper compatível com `theme.breakpoints` para projetos que usam ThemeProvider
 * customizado e querem reagir aos breakpoints MUI nativos (xs/sm/md/lg/xl).
 */
export function useIsBelowBreakpoint(breakpoint: "xs" | "sm" | "md" | "lg" | "xl"): boolean {
	const theme = useTheme();
	return useMediaQuery(theme.breakpoints.down(breakpoint), { noSsr: true });
}
